import { Async } from '@client/new/helpers/types';
import { useInfluencer } from '@client/new/hooks/use-influencer';
import { isAfterOrSameDay } from '@client/new/pages/promotions/view-model/utils';

import { OtpPayouts, useOtpPayouts } from './useOtpPayouts';
import {
  SubscriptionPayouts,
  useSubscriptionPayouts,
} from './useSubscriptionPayouts';

export type usePayoutsProps = {
  date: Date;
  creatorId: number;
};

export type PayoutsBreakdown = {
  subscription: SubscriptionPayouts;
  otp: OtpPayouts;
  exportEnabled: boolean;
};

export const usePayouts = ({
  date,
  creatorId,
}: usePayoutsProps): Async<PayoutsBreakdown> => {
  const subscriptionPayouts = useSubscriptionPayouts({ date, creatorId });
  const otpPayouts = useOtpPayouts({ date, creatorId });
  const {
    isLoading: isInfluencerLoading,
    influencer,
    error: influencerError,
  } = useInfluencer({ creatorId });

  if (
    subscriptionPayouts.type === 'fetch-error' ||
    otpPayouts.type === 'fetch-error' ||
    !!influencerError
  ) {
    return {
      type: 'fetch-error',
      value: 'could not fetch data',
    };
  }

  if (
    !isInfluencerLoading &&
    !!influencer &&
    subscriptionPayouts.type === 'fetched' &&
    otpPayouts.type === 'fetched'
  ) {
    const hasOtpPayout = otpPayouts.value.totalPayout !== 0;
    const hasSubPayout = subscriptionPayouts.value.totalPayout !== 0;
    const hasAnyPayout = hasOtpPayout || hasSubPayout;

    const isOtpPaid = hasOtpPayout ? !!otpPayouts.value.paidAt : true;
    const isSubPaid = hasSubPayout ? !!subscriptionPayouts.value.paidAt : true;

    const payoutsAreSettled =
      import.meta.env.VITE_ENV === 'production' ? isOtpPaid && isSubPaid : true;

    const isTransactionLedgerEnabled =
      influencer.customizations?.isTransactionLedgerEnabled ?? false;

    return {
      type: 'fetched',
      value: {
        subscription: subscriptionPayouts.value,
        otp: otpPayouts.value,
        exportEnabled:
          isTransactionLedgerEnabled &&
          hasAnyPayout &&
          payoutsAreSettled &&
          isAfterOrSameDay(date, new Date('2025-05-01')),
      },
    };
  }

  return {
    type: 'fetching',
  };
};
